'use client';

import { useCallback, useEffect, useState } from 'react';

import { useSecureQuery } from '@/shared/hooks/apis/useApiHooks';
import { authStorage } from '@/shared/libs/authStorage';
import logger from '@/shared/libs/logger.client';
import { previewScope } from '@/shared/libs/previewScope';
import { useAuthStore } from '@/shared/store/authStore';
import { Capabilities, ProfileType } from '@/shared/types/capabilities';
import { InternalProfile } from '@/shared/types/internal-profile';
import { normalizeUserRoles } from '@/shared/utils/roles';

export type MeDto = {
  id?: string;
  accountId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  roles?: string[];
  permissions?: string[];
  organizationId?: string;
  avatarUrl?: string;
  companyName?: string | null;
  consultantId?: string | null;
  /**
   * Fonction occupée dans l'organisation, pour un compte interne (suivora-api#1356).
   *
   * **Absent** tant que l'API ne la sert pas : l'écran retombe alors sur le rôle et l'annonce comme
   * tel, plutôt que de nommer « fonction » ce qui n'en est pas une (#1805).
   */
  jobTitle?: string | null;
  profileType?: ProfileType;
  capabilities?: Capabilities;
  contractStart?: string | null;
  contractEnd?: string | null;
  internal?: InternalProfile | null;
  /**
   * Minutes d'inactivité avant coupure, réglage de l'organisation (suivora-api#887).
   *
   * Trois valeurs, trois sens : **absent** = l'API ne le sert pas encore, on n'affiche aucune
   * échéance ; `null` ou `0` = aucune coupure configurée ; un nombre = le délai.
   *
   * Il est lu ici et pas depuis les paramètres d'administration : ce réglage s'applique à tout le
   * monde, et la route des paramètres demande `settings.manage`. Un salarié porté — celui qui
   * remplit un CRA, donc celui qui perd le plus à être déconnecté — ne peut pas la lire.
   */
  sessionIdleTimeoutMinutes?: number | null;
};

type Options = {
  enabled?: boolean;
  staleTime?: number;
};

const ME_QUERY_KEY = ['users', 'me'] as const;

export function useFetchMe(options: Options = {}) {
  const { enabled = false, staleTime } = options;

  const { setUser } = useAuthStore();
  const [isReady, setIsReady] = useState(false);

  const query = useSecureQuery<MeDto>(ME_QUERY_KEY, '/api/v1/auth/me', {
    enabled,
    staleTime,
  });

  const { data: currentUser, error, isLoading, isFetching } = query;

  useEffect(() => {
    if (!currentUser) return;
    // Aperçu démo : `/auth/me` renvoie le consultant fictif — ne jamais l'écrire
    // dans le store, sinon la session admin serait corrompue.
    if (previewScope.getToken()) return;
    const id = currentUser.id ?? currentUser.accountId;
    if (!id) return;
    setUser({
      id,
      email: currentUser.email,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      fullName: currentUser.fullName,
      avatarUrl: currentUser.avatarUrl,
      companyName: currentUser.companyName ?? undefined,
      consultantId: currentUser.consultantId ?? undefined,
      jobTitle: currentUser.jobTitle ?? undefined,
      roles: normalizeUserRoles(currentUser.roles),
      permissions: currentUser.permissions,
      orgId: currentUser.organizationId,
      profileType: currentUser.profileType,
      capabilities: currentUser.capabilities,
      contractStart: currentUser.contractStart,
      contractEnd: currentUser.contractEnd,
      internal: currentUser.internal,
    });
    /*
      **Rétablir un état cohérent, pas seulement l'utilisateur** (#1571).

      `isAuthenticated` vaut `!!(user && accessToken)`. On n'écrivait que l'utilisateur : si le jeton
      avait disparu de l'état — ce que #1568 a corrigé à sa source, mais qui peut arriver autrement —
      la session restait à mi-chemin. Toutes les gardes lisaient alors « non authentifié » pour une
      session qui venait pourtant de répondre, et la page restait blanche jusqu'au rechargement.

      Cette requête a **abouti** : un jeton valide existait donc au moment de l'appel. Le relire
      depuis le stockage et le réinscrire remet les deux moitiés d'accord. Sans jeton en stockage on
      ne fabrique rien : c'est alors une session qui tient à un cookie de rafraîchissement, et
      inventer une valeur serait pire que le déséquilibre.
    */
    const { accessToken, setAccessToken } = useAuthStore.getState();
    if (!accessToken) {
      const stored = authStorage.getAccessToken();
      if (stored) setAccessToken(stored);
    }

    setIsReady(true);
    logger.info('[useFetchMe] User fetched successfully', { id });
  }, [currentUser, setUser]);

  useEffect(() => {
    if (error) {
      logger.error('[useFetchMe] Failed to fetch current user', { error });
      setIsReady(true);
    }
  }, [error]);

  const refreshMe = useCallback(async () => {
    logger.debug('[useFetchMe] Refreshing /me endpoint');
    const res = await query.refetch();
    return res.data ?? null;
  }, [query]);

  return {
    isReady,
    isHydrating: isLoading || isFetching,
    currentUser,
    refreshMe,
    ...query,
  };
}
